import { createContext, useCallback, useContext, useLayoutEffect, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { publicAsset } from '@/components/navigation/navigationAssets'
import { useCandidateData } from '@/components/candidates/CandidateDataProvider'

const LoadingContext = createContext<(token: symbol, loading: boolean) => void>(() => {})

export function PageLoadingProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Set<symbol>>(() => new Set())
  const [mounted, setMounted] = useState(false)
  const [slow, setSlow] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const { status } = useCandidateData()
  const loading = !mounted || pending.size > 0 || status === 'loading'
  const active = loading && !dismissed
  const report = useCallback((token: symbol, busy: boolean) => {
    setPending((current) => {
      if (current.has(token) === busy) return current
      const next = new Set(current)
      if (busy) next.add(token)
      else next.delete(token)
      return next
    })
  }, [])

  useLayoutEffect(() => {
    setMounted(true)
    document.getElementById('app-loader')?.remove()
  }, [])

  useEffect(() => {
    if (!active) return
    const timeout = window.setTimeout(() => setSlow(true), 12_000)
    return () => window.clearTimeout(timeout)
  }, [active])

  useLayoutEffect(() => {
    if (!active) return
    const root = document.getElementById('root')!
    const wasInert = root.inert
    const overflow = document.body.style.overflow
    root.inert = true
    document.body.style.overflow = 'hidden'
    return () => {
      root.inert = wasInert
      document.body.style.overflow = overflow
    }
  }, [active])

  return (
    <LoadingContext.Provider value={report}>
      {children}
      {active && createPortal(
        <div className="app-loader" data-page-loader role="status" aria-live="polite">
          <div className="app-loader__content">
            <img className="app-loader__logo" src={publicAsset('images/brand/Logo-et-horizontal-blanco.svg')} alt="El Telégrafo" />
            <span className="app-loader__spinner" aria-hidden="true" />
            <span className="app-loader__label">Cargando información de la página</span>
            {slow && <button className="app-loader__continue" type="button" onClick={() => setDismissed(true)}>Continuar mientras carga</button>}
          </div>
        </div>, document.body,
      )}
    </LoadingContext.Provider>
  )
}

export function usePageLoading(loading: boolean) {
  const report = useContext(LoadingContext)
  const [token] = useState(() => Symbol('page-data'))
  useLayoutEffect(() => {
    report(token, loading)
    return () => report(token, false)
  }, [loading, report, token])
}

export function PageLoadingFallback() {
  usePageLoading(true)
  return <p role="status">Cargando página…</p>
}
